/**
 * A QR encoder, written out rather than installed.
 *
 * The invite screen hands somebody a 32-character base32 secret and asks them
 * to type it into a phone. That is the most error-prone moment in getting a
 * new person onto the platform: a mistyped key produces an authenticator that
 * generates plausible six-digit codes which are all wrong, and the failure
 * surfaces later, at the sign-in screen, looking like a forgotten password.
 * A QR code removes the typing.
 *
 * It is hand-rolled for the same reason scrypt and TOTP in `src/auth.ts` are:
 * this is a small, frozen, fully-specified algorithm (ISO/IEC 18004), and a
 * dependency that renders a credential is a dependency with its hands on a
 * credential. Nothing here reaches the network, reads the DOM, or holds
 * state — `encodeQr` is a pure function from a string to a matrix of booleans.
 *
 * Byte mode only, UTF-8. Numeric, alphanumeric and kanji modes would pack an
 * otpauth:// URL slightly tighter and are three more things to get wrong for
 * a payload already comfortably inside version 10.
 *
 * Verified against two independent implementations rather than by inspection:
 * the matrices this produces are identical, module for module, to the `qrcode`
 * package's, and a real decoder (`jsQR`) reads the rendered output back as the
 * exact input string. See `test/qr.test.ts` for the frozen vectors that keep
 * it that way.
 */

export type EccLevel = "L" | "M" | "Q" | "H";

export interface QrMatrix {
  /** Width and height in modules, excluding the quiet zone. */
  readonly size: number;
  /** `modules[y][x]` — true is dark. */
  readonly modules: readonly boolean[][];
  readonly version: number;
  readonly ecc: EccLevel;
}

/** The two-bit field the format information carries. Not in L/M/Q/H order. */
const ECC_FORMAT_BITS: Record<EccLevel, number> = { L: 1, M: 0, Q: 3, H: 2 };
const ECC_INDEX: Record<EccLevel, number> = { L: 0, M: 1, Q: 2, H: 3 };

/**
 * Error-correction codewords per block, and number of blocks, by version.
 * Straight out of the tables in the standard; index 0 is unused padding so
 * that the array index is the version number.
 */
const ECC_CODEWORDS_PER_BLOCK: readonly (readonly number[])[] = [
  // ver: 1   2   3   4   5   6   7   8   9  10  11  12  13  14  15  16  17  18  19  20  21  22  23  24  25  26  27  28  29  30  31  32  33  34  35  36  37  38  39  40
  [-1, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28, 28, 28, 30, 30, 26, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30], // L
  [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28], // M
  [-1, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24, 28, 26, 24, 20, 30, 24, 28, 28, 26, 30, 28, 30, 30, 30, 30, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30], // Q
  [-1, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28, 24, 28, 22, 24, 24, 30, 28, 28, 26, 28, 30, 24, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30], // H
];

const NUM_ERROR_CORRECTION_BLOCKS: readonly (readonly number[])[] = [
  [-1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9, 10, 12, 12, 12, 13, 14, 15, 16, 17, 18, 19, 19, 20, 21, 22, 24, 25], // L
  [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49], // M
  [-1, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8, 8, 10, 12, 16, 12, 17, 16, 18, 21, 20, 23, 23, 25, 27, 29, 34, 34, 35, 38, 40, 43, 45, 48, 51, 53, 56, 59, 62, 65, 68], // Q
  [-1, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8, 11, 11, 16, 16, 18, 16, 19, 21, 25, 25, 25, 34, 30, 32, 35, 37, 40, 42, 45, 48, 51, 54, 57, 60, 63, 66, 70, 74, 77, 81], // H
];

const MIN_VERSION = 1;
const MAX_VERSION = 40;

// --------------------------------------------------------------- capacity ---

/** Total modules available to data and error correction, function patterns removed. */
function rawDataModules(version: number): number {
  let result = (16 * version + 128) * version + 64;
  if (version >= 2) {
    const numAlign = Math.floor(version / 7) + 2;
    result -= (25 * numAlign - 10) * numAlign - 55;
    if (version >= 7) result -= 36; // the two version-information blocks
  }
  return result;
}

/** Codewords left for the message once error correction has taken its share. */
function dataCodewords(version: number, ecc: EccLevel): number {
  const e = ECC_INDEX[ecc];
  return (
    Math.floor(rawDataModules(version) / 8) -
    ECC_CODEWORDS_PER_BLOCK[e][version] * NUM_ERROR_CORRECTION_BLOCKS[e][version]
  );
}

// ------------------------------------------------------- Reed-Solomon (GF) ---

/** Multiply in GF(2^8) modulo the QR primitive polynomial x^8 + x^4 + x^3 + x^2 + 1. */
function gfMultiply(x: number, y: number): number {
  let z = 0;
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z & 0xff;
}

/** Coefficients of the generator polynomial of the given degree, highest term omitted. */
function rsDivisor(degree: number): Uint8Array {
  const result = new Uint8Array(degree);
  result[degree - 1] = 1;
  let root = 1;
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < degree; j++) {
      result[j] = gfMultiply(result[j], root);
      if (j + 1 < degree) result[j] ^= result[j + 1];
    }
    root = gfMultiply(root, 0x02);
  }
  return result;
}

function rsRemainder(data: Uint8Array, divisor: Uint8Array): Uint8Array {
  const result = new Uint8Array(divisor.length);
  for (const b of data) {
    const factor = b ^ result[0];
    result.copyWithin(0, 1);
    result[result.length - 1] = 0;
    for (let i = 0; i < result.length; i++) {
      result[i] ^= gfMultiply(divisor[i], factor);
    }
  }
  return result;
}

// ------------------------------------------------------------ bit assembly ---

class BitBuffer {
  readonly bits: number[] = [];

  append(value: number, length: number): void {
    for (let i = length - 1; i >= 0; i--) this.bits.push((value >>> i) & 1);
  }
}

/**
 * Message codewords: mode indicator, length, payload, terminator, padding.
 * The alternating 0xEC/0x11 pad bytes are prescribed, not arbitrary filler —
 * a decoder uses them to tell padding from data.
 */
function buildCodewords(
  data: Uint8Array,
  version: number,
  ecc: EccLevel,
): Uint8Array {
  const capacityBits = dataCodewords(version, ecc) * 8;
  const bb = new BitBuffer();

  bb.append(0b0100, 4); // byte mode
  bb.append(data.length, version <= 9 ? 8 : 16);
  for (const b of data) bb.append(b, 8);

  bb.append(0, Math.min(4, capacityBits - bb.bits.length)); // terminator
  bb.append(0, (8 - (bb.bits.length % 8)) % 8); // up to a whole byte
  for (let pad = 0xec; bb.bits.length < capacityBits; pad ^= 0xec ^ 0x11) {
    bb.append(pad, 8);
  }

  const out = new Uint8Array(bb.bits.length / 8);
  bb.bits.forEach((bit, i) => {
    out[i >>> 3] |= bit << (7 - (i & 7));
  });
  return out;
}

/**
 * Split into blocks, give each its own error correction, then interleave.
 * The interleaving is what lets a QR code survive a thumb over one corner: a
 * contiguous smudge then damages a few codewords of every block rather than
 * destroying one block outright.
 */
function addEccAndInterleave(
  data: Uint8Array,
  version: number,
  ecc: EccLevel,
): Uint8Array {
  const e = ECC_INDEX[ecc];
  const numBlocks = NUM_ERROR_CORRECTION_BLOCKS[e][version];
  const eccLen = ECC_CODEWORDS_PER_BLOCK[e][version];
  const rawCodewords = Math.floor(rawDataModules(version) / 8);
  const shortBlocks = numBlocks - (rawCodewords % numBlocks);
  const shortLength = Math.floor(rawCodewords / numBlocks);

  const divisor = rsDivisor(eccLen);
  const blocks: Uint8Array[] = [];
  for (let i = 0, offset = 0; i < numBlocks; i++) {
    const short = i < shortBlocks;
    const dataLen = shortLength - eccLen + (short ? 0 : 1);
    const dat = data.subarray(offset, offset + dataLen);
    offset += dataLen;

    // Every block is built to the same length — the short ones carry an unused
    // slot where the long ones carry their extra data codeword — so that the
    // interleave below can walk one rectangular grid. The slot is never
    // emitted; it exists to keep the error-correction codewords in column.
    const block = new Uint8Array(shortLength + 1);
    block.set(dat);
    block.set(rsRemainder(dat, divisor), dataLen + (short ? 1 : 0));
    blocks.push(block);
  }

  const result = new Uint8Array(rawCodewords);
  let k = 0;
  for (let i = 0; i < shortLength + 1; i++) {
    blocks.forEach((block, j) => {
      if (i !== shortLength - eccLen || j >= shortBlocks) result[k++] = block[i];
    });
  }
  return result;
}

// ----------------------------------------------------------------- drawing ---

type Grid = boolean[][];

function alignmentPositions(version: number): number[] {
  if (version === 1) return [];
  const count = Math.floor(version / 7) + 2;
  const step =
    version === 32 ? 26 : Math.ceil((version * 4 + 4) / (count * 2 - 2)) * 2;
  const result = [6];
  for (let pos = version * 4 + 10; result.length < count; pos -= step) {
    result.splice(1, 0, pos);
  }
  return result;
}

/**
 * Everything that is not message: the three finders, the timing lines, the
 * alignment patterns, the dark module, and the areas reserved for format and
 * version information. `isFunction` is kept alongside so that the data pass
 * knows which modules it may not write to, and the mask knows which to leave
 * alone.
 */
function drawFunctionPatterns(
  modules: Grid,
  isFunction: Grid,
  version: number,
  ecc: EccLevel,
): void {
  const size = modules.length;

  const set = (x: number, y: number, dark: boolean) => {
    if (x < 0 || y < 0 || x >= size || y >= size) return;
    modules[y][x] = dark;
    isFunction[y][x] = true;
  };

  // Timing lines first; the finders overwrite them where they cross.
  for (let i = 0; i < size; i++) {
    set(6, i, i % 2 === 0);
    set(i, 6, i % 2 === 0);
  }

  // Finder patterns with their separators: a 7x7 target in three corners.
  for (const [cx, cy] of [
    [3, 3],
    [size - 4, 3],
    [3, size - 4],
  ]) {
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const ring = Math.max(Math.abs(dx), Math.abs(dy));
        set(cx + dx, cy + dy, ring !== 2 && ring !== 4);
      }
    }
  }

  // Alignment patterns: 5x5 wherever the grid says, except where one would
  // collide with a finder.
  const positions = alignmentPositions(version);
  for (let i = 0; i < positions.length; i++) {
    for (let j = 0; j < positions.length; j++) {
      const atFinder =
        (i === 0 && j === 0) ||
        (i === 0 && j === positions.length - 1) ||
        (i === positions.length - 1 && j === 0);
      if (atFinder) continue;
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          set(
            positions[j] + dx,
            positions[i] + dy,
            Math.max(Math.abs(dx), Math.abs(dy)) !== 1,
          );
        }
      }
    }
  }

  // Reserve the format areas; the real bits are written once a mask is chosen.
  drawFormatBits(modules, isFunction, ecc, 0);

  // Version information, two 3x6 blocks, from version 7 up.
  if (version >= 7) {
    let rem = version;
    for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
    const bits = (version << 12) | rem;
    for (let i = 0; i < 18; i++) {
      const dark = ((bits >>> i) & 1) !== 0;
      const a = size - 11 + (i % 3);
      const b = Math.floor(i / 3);
      set(a, b, dark);
      set(b, a, dark);
    }
  }
}

/** The 15-bit format word — error-correction level and mask — written twice. */
function drawFormatBits(
  modules: Grid,
  isFunction: Grid,
  ecc: EccLevel,
  mask: number,
): void {
  const size = modules.length;
  const data = (ECC_FORMAT_BITS[ecc] << 3) | mask;
  let rem = data;
  for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
  const bits = ((data << 10) | rem) ^ 0x5412;

  const set = (x: number, y: number, dark: boolean) => {
    modules[y][x] = dark;
    isFunction[y][x] = true;
  };
  const bit = (i: number) => ((bits >>> i) & 1) !== 0;

  // First copy, around the top-left finder.
  for (let i = 0; i <= 5; i++) set(8, i, bit(i));
  set(8, 7, bit(6));
  set(8, 8, bit(7));
  set(7, 8, bit(8));
  for (let i = 9; i < 15; i++) set(14 - i, 8, bit(i));

  // Second copy, split between the other two finders.
  for (let i = 0; i < 8; i++) set(size - 1 - i, 8, bit(i));
  for (let i = 8; i < 15; i++) set(8, size - 15 + i, bit(i));

  set(8, size - 8, true); // the dark module: always set, always here
}

/**
 * The message, written in a two-module-wide boustrophedon from the bottom
 * right, stepping over every function module on the way.
 */
function drawCodewords(
  modules: Grid,
  isFunction: Grid,
  codewords: Uint8Array,
): void {
  const size = modules.length;
  let i = 0; // bit index into the codeword stream

  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5; // the vertical timing line is not a data column
    for (let vert = 0; vert < size; vert++) {
      for (let j = 0; j < 2; j++) {
        const x = right - j;
        const upward = ((right + 1) & 2) === 0;
        const y = upward ? size - 1 - vert : vert;
        if (!isFunction[y][x] && i < codewords.length * 8) {
          modules[y][x] = ((codewords[i >>> 3] >>> (7 - (i & 7))) & 1) !== 0;
          i++;
        }
        // Anything left over stays light: the standard remainder bits.
      }
    }
  }
}

function maskAt(mask: number, x: number, y: number): boolean {
  switch (mask) {
    case 0:
      return (x + y) % 2 === 0;
    case 1:
      return y % 2 === 0;
    case 2:
      return x % 3 === 0;
    case 3:
      return (x + y) % 3 === 0;
    case 4:
      return (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0;
    case 5:
      return ((x * y) % 2) + ((x * y) % 3) === 0;
    case 6:
      return (((x * y) % 2) + ((x * y) % 3)) % 2 === 0;
    case 7:
      return (((x + y) % 2) + ((x * y) % 3)) % 2 === 0;
    default:
      throw new Error(`mask ${mask} is not one of the eight`);
  }
}

function applyMask(modules: Grid, isFunction: Grid, mask: number): void {
  const size = modules.length;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (!isFunction[y][x] && maskAt(mask, x, y)) modules[y][x] = !modules[y][x];
    }
  }
}

/**
 * The four penalty rules, scoring how hard a masked matrix is for a scanner
 * to read: long same-colour runs, solid 2x2 blocks, anything imitating a
 * finder pattern, and an unbalanced ratio of dark to light. Lowest wins.
 */
function penalty(modules: Grid): number {
  const size = modules.length;
  let result = 0;

  const runScore = (run: number) => (run >= 5 ? run - 2 : 0);

  for (let y = 0; y < size; y++) {
    let run = 1;
    for (let x = 1; x <= size; x++) {
      if (x < size && modules[y][x] === modules[y][x - 1]) run++;
      else {
        result += runScore(run);
        run = 1;
      }
    }
  }
  for (let x = 0; x < size; x++) {
    let run = 1;
    for (let y = 1; y <= size; y++) {
      if (y < size && modules[y][x] === modules[y - 1][x]) run++;
      else {
        result += runScore(run);
        run = 1;
      }
    }
  }

  for (let y = 0; y < size - 1; y++) {
    for (let x = 0; x < size - 1; x++) {
      const c = modules[y][x];
      if (
        c === modules[y][x + 1] &&
        c === modules[y + 1][x] &&
        c === modules[y + 1][x + 1]
      ) {
        result += 3;
      }
    }
  }

  // 1:1:3:1:1 with four light modules on one side — the finder signature.
  // An eleven-module window that must fit wholly inside the symbol, which is
  // what the standard describes and what every encoder I compared against
  // does: the quiet zone is deliberately not treated as the light run, so a
  // pattern hard against the border does not score.
  const target = [true, false, true, true, true, false, true];
  const allLight = (line: boolean[], from: number): boolean => {
    if (from < 0 || from + 4 > line.length) return false;
    for (let k = from; k < from + 4; k++) if (line[k]) return false;
    return true;
  };
  const finderLike = (line: boolean[]): number => {
    let hits = 0;
    for (let i = 0; i + 7 <= line.length; i++) {
      if (!target.every((v, k) => line[i + k] === v)) continue;
      if (allLight(line, i - 4)) hits++;
      if (allLight(line, i + 7)) hits++;
    }
    return hits * 40;
  };
  for (let y = 0; y < size; y++) result += finderLike(modules[y]);
  for (let x = 0; x < size; x++) result += finderLike(modules.map((row) => row[x]));

  // Dark/light balance: ten points for every five percent the proportion of
  // dark modules strays from half, as the standard puts it — 50 +/- 5k.
  let dark = 0;
  for (const row of modules) for (const c of row) if (c) dark++;
  const total = size * size;
  const k = Math.abs(Math.ceil((dark * 100) / total / 5) - 10);
  result += k * 10;

  return result;
}

// -------------------------------------------------------------------- API ---

/**
 * Encode text as a QR matrix, choosing the smallest version that fits.
 *
 * Throws only if the text genuinely cannot be represented — roughly 2.3kB at
 * level M. An otpauth:// URL is two orders of magnitude inside that.
 */
export function encodeQr(text: string, ecc: EccLevel = "M"): QrMatrix {
  const data = new TextEncoder().encode(text);

  let version = MIN_VERSION;
  for (; ; version++) {
    if (version > MAX_VERSION) {
      throw new Error(
        `${data.length} bytes is too long for a QR code at level ${ecc}`,
      );
    }
    // Mode indicator and character count cost one byte below version 10 and
    // two above it, plus the four-bit mode nibble.
    const overhead = version <= 9 ? 2 : 3;
    if (data.length + overhead <= dataCodewords(version, ecc)) break;
  }

  const size = version * 4 + 17;
  const modules: Grid = Array.from({ length: size }, () =>
    new Array<boolean>(size).fill(false),
  );
  const isFunction: Grid = Array.from({ length: size }, () =>
    new Array<boolean>(size).fill(false),
  );

  drawFunctionPatterns(modules, isFunction, version, ecc);
  drawCodewords(
    modules,
    isFunction,
    addEccAndInterleave(buildCodewords(data, version, ecc), version, ecc),
  );

  let bestMask = 0;
  let bestPenalty = Infinity;
  for (let mask = 0; mask < 8; mask++) {
    applyMask(modules, isFunction, mask);
    drawFormatBits(modules, isFunction, ecc, mask);
    const score = penalty(modules);
    if (score < bestPenalty) {
      bestPenalty = score;
      bestMask = mask;
    }
    applyMask(modules, isFunction, mask); // undo: the mask is its own inverse
  }
  applyMask(modules, isFunction, bestMask);
  drawFormatBits(modules, isFunction, ecc, bestMask);

  return { size, modules, version, ecc };
}

/**
 * The dark modules as one SVG path, in a viewBox of `size + 2 * quiet` units.
 *
 * One path rather than a few hundred rects, because the matrix is decoration
 * to the DOM and nothing good comes of putting 400 nodes in it. The quiet zone
 * is part of the geometry rather than a margin: a scanner needs four clear
 * modules around the symbol, and CSS padding is too easy for a later layout
 * change to take away.
 */
export function qrSvgPath(matrix: QrMatrix, quiet = 4): string {
  const parts: string[] = [];
  for (let y = 0; y < matrix.size; y++) {
    for (let x = 0; x < matrix.size; x++) {
      if (matrix.modules[y][x]) parts.push(`M${x + quiet} ${y + quiet}h1v1h-1z`);
    }
  }
  return parts.join("");
}
